import { DropdownOption } from 'components/final-form/dropdownUtils';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { Button, Header, RadioProps } from 'semantic-ui-react';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import { RouterProps } from 'react-router';
import { MandateDto, ProcedureStatisticRequest, TreaterDto } from 'ts-types/api.types';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import moment from 'moment';
import styled from 'styled-components';
import { noop } from 'utils/functionUtils';
import { getAllTreaters } from 'services/treaterServices';
import { ProcedureStatisticSortBy } from 'ts-types/api.enums';
import { FormApi, FormState } from 'final-form';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import DatePicker from 'components/final-form/DatePicker';
import Radio from 'components/final-form/Radio';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import Select from 'components/final-form/Select';
import { exportProcedureStatistic } from 'services/procedureStatisticSerivce';
import { getAllMandatesWithDeleted } from 'services/mandateServices';

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const PraxFormSegment = styled(PraxStyledSegment)`
  flex-grow: 0;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  .ui.header.tiny {
    font-size: 1em !important;
  }
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 40px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const ProceduresLabel = styled.label`
  display: inline-block;
  min-width: 100px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchGroup = styled.div`
  flex: 1 1 auto;
  max-width: 350px;
  min-width: 300px;

`;

const SearchButtonContainer = styled.div`
  flex: 1 1 100%;
  margin-top: 1rem;
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;

const SearchSelectionField = styled.div`
  margin-bottom: 7px;

  .field {
    display: inline-block !important;
    min-width: 17rem;
  }
`;

const RadioBtnsContainer = styled.div`
  margin-top: 10px;

  .field {
    min-width: unset;
  }

  .field + .field {
    margin-left: 2.5rem;
  }
`;

interface ProcedureStatisticFormData extends ProcedureStatisticRequest {
  treaterSelection: boolean,
  mandateSelection: boolean
}

interface Props extends AuthConsumerRenderProps, MessageSourceProps, RouterProps {
}

interface State {
  treaters: Array<DropdownOption>,
  mandates: Array<DropdownOption>,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class ProcedureStatisticView extends Component<Props, State> {

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();

  constructor(props: Props) {
    super(props);

    const {radioMap} = this;
    const {getMessage} = props;

    const cancelTokenSource = axios.CancelToken.source();

    const sortByValues = Object.values(ProcedureStatisticSortBy);

    const radioDefinitions = generateRadioButtonOptionsFromEnum(sortByValues, "sortBy", getMessage);

    radioDefinitions.forEach((radioDefinition, index) => {
      radioMap.set(sortByValues[index], radioDefinition);
    });

    this.state = {
      treaters: [],
      mandates: [],
      cancelTokenSource: cancelTokenSource
    };

    let methods: Array<any> = [getAllTreaters(cancelTokenSource), getAllMandatesWithDeleted(cancelTokenSource)];

    Promise.all(methods)
        .then(responses => {

          const treaters = responses[0];
          const mappedTreaters: Array<DropdownOption> = treaters.map((treater: TreaterDto) => ({
            key: treater.id,
            text: treater.username,
            value: treater.id
          }));

          const mandates = responses[1];
          const mappedMandates: Array<DropdownOption> = mandates.map((madate: MandateDto) => ({
            key: madate.id,
            text: madate.name,
            value: madate.id
          }));

          this.setState({
            treaters: mappedTreaters,
            mandates: mappedMandates
          })
        })
        .catch(() => {
          this.setErrorMessage(getMessage("error.general"));
        })
        .finally(noop);
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    });
  };

  initialSearchValues = (): Partial<ProcedureStatisticFormData> => {

    const {currentUser, options: {workDate}} = this.props;

    const startDate = moment(workDate).format("YYYY-MM-DD");

    return {
      mandateId: currentUser ? currentUser.userConfig.mandateId : 1,
      fromDate: startDate,
      toDate: startDate,
      groupBy: false,
      mandateSelection: true,
      treaterSelection: false,
      sortBy: ProcedureStatisticSortBy.PROCEDURE_CODE
    }
  };

  handleChange = ({values}: FormState, form: FormApi) => {
    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }

    const treaterSelection: boolean = values.treaterSelection;

    if (!treaterSelection) {
      form.change("treaterId", undefined);
    }

    const mandateSelection: boolean = values.mandateSelection;

    if (!mandateSelection) {
      form.change("mandateId", undefined);
    }
  };

  exportFile = async (values: any) => {

    const {
      getMessage
    } = this.props;

    const {cancelTokenSource} = this.state;

    let request: Partial<ProcedureStatisticRequest> = {
      fromDate: values.fromDate,
      toDate: values.toDate,
      treaterId: values.treaterId,
      mandateId: values.mandateId,
      procedureCode: values.procedureCode,
      sortBy: values.sortBy
    };

    await exportProcedureStatistic(request, cancelTokenSource)
        .then(noop)
        .catch(() => this.setErrorMessage(getMessage("error.general")));
  };

  onEnterKeyPressed = (e: any, values: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      this.exportFile(values)
    }
  };

  render(): React.ReactElement {

    const {
      errorMessage
    } = this.state;

    return (
        <PraxFormSegment>
          <MainContainer>
            {errorMessage
            && (
                <StyledErrorMessage onDismiss={() => this.setErrorMessage()} style={{margin: 0}}>
                  {errorMessage}
                </StyledErrorMessage>
            )
            }
            <FinalForm
                onSubmit={() => noop}
                initialValues={this.initialSearchValues()}
                subscription={{values: true, pristine: true}}
                render={this.renderForm}
            />
          </MainContainer>
        </PraxFormSegment>
    );
  }

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    const {radioMap, props, state} = this;
    const {getMessage} = props;
    const {treaters, mandates} = state;

    const disabledBefore = values.fromDate && new Date(values.fromDate);
    const disabledTreaters = !values.treaterSelection;
    const disabledMandates = !values.mandateSelection;

    const procedureCode = radioMap.get(ProcedureStatisticSortBy.PROCEDURE_CODE);
    const time = radioMap.get(ProcedureStatisticSortBy.TIME);

    const hasValidationErrors = form.getState().hasValidationErrors;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <Header>{getMessage("searchOptions")}</Header>
          </StyledContainer>
          <StyledContainer style={{marginTop: 10}}
                           onKeyDown={(e) => this.onEnterKeyPressed(e, values)}>
            <SearchOptionsContainer>
              <SearchGroup className="noMargin">
                <Header size="tiny">{getMessage("paymentJournal.timePeriod")}</Header>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromDate"
                      component={DatePicker}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toDate"
                      component={DatePicker}
                      disabledBefore={disabledBefore}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("medicament.selection")}</Header>
                <SearchSelectionField>
                  <Field
                      name="mandateSelection"
                      component={CheckBox}
                      label={getMessage("procedureStatistic.mandateSelction")}
                  />
                </SearchSelectionField>
                <SearchSelectionField>
                  <Field
                      name="mandateId"
                      component={Select}
                      options={mandates}
                      disabled={disabledMandates}
                      fluid
                  />
                </SearchSelectionField>
                <SearchSelectionField>
                  <Field
                      name="treaterSelection"
                      component={CheckBox}
                      label={getMessage("dayStatistic.treaterSelection")}
                  />
                </SearchSelectionField>
                <SearchSelectionField>
                  <Field
                      name="treaterId"
                      component={Select}
                      options={treaters}
                      disabled={disabledTreaters}
                      fluid
                  />
                </SearchSelectionField>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("procedures")}</Header>
                <SearchField>
                  <ProceduresLabel className="margin">
                    {getMessage("procedure.code")}:
                  </ProceduresLabel>
                  <Field
                      name="procedureCode"
                      component={Input}
                      fluid
                  />
                </SearchField>
                {
                  /**
                   * TODO KJ: In case we implement output this fields will be used
                   */
                }
                {/*<SearchField>*/}
                {/*  <ProceduresLabel className="margin">*/}
                {/*    {getMessage("procedureStatistic.totalWorkTime")}:*/}
                {/*  </ProceduresLabel>*/}
                {/*  <Field*/}
                {/*      name="totalWorkTime"*/}
                {/*      component={TimeInput}*/}
                {/*      fluid*/}
                {/*  />*/}
                {/*</SearchField>*/}
                {/*<SearchField>*/}
                {/*  <ProceduresLabel/>*/}
                {/*  <Field*/}
                {/*      name="groupBy"*/}
                {/*      component={CheckBox}*/}
                {/*      label={getMessage("procedureStatistic.groupBy")}*/}
                {/*  />*/}
                {/*</SearchField>*/}
                <RadioBtnsContainer>
                  <ProceduresLabel className="margin">
                    {getMessage("dayStatistic.sortBy")}:
                  </ProceduresLabel>
                  <Field
                      name="sortBy"
                      component={Radio}
                      radioDefinition={procedureCode}
                  />
                  <Field
                      name="sortBy"
                      component={Radio}
                      radioDefinition={time}
                  />
                </RadioBtnsContainer>
              </SearchGroup>
              <SearchButtonContainer>
                <Button
                    type="button" primary compact
                    onClick={() => this.exportFile(values)}
                    disabled={hasValidationErrors}
                >
                  {getMessage("procedureStatistic.export")}
                </Button>
              </SearchButtonContainer>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </StyledForm>
    )
  };

}

export default withAuthContext(withMessages(ProcedureStatisticView));
